import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  ArrowDownload20Regular,
  ArrowSync20Regular,
  ChevronDown20Regular,
  CheckmarkCircle20Regular,
  Dismiss20Regular,
  Edit20Regular,
  Search20Regular,
  DocumentPdf20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { getCompanies, type Company } from "../../companies/companiesApi";
import { Modal } from "../../components/Modal";
import { DocumentViewer, type ViewerDocument } from "../../components/DocumentViewer";
import { Tooltip } from "../../components/Tooltip";
import { APP_LOCALE } from "../../config/locale";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  ApiError,
  exportRevenueInvoices,
  fetchInvoiceDocument,
  getApprovalStatuses,
  getInvoiceFilterOptions,
  getRevenueInvoices,
  getWorks,
  setInvoiceApprovalStatus,
  setInvoiceSummaryExclusion,
  setInvoicesSummaryExclusion,
  updateInvoice,
  type ApprovalStatusOption,
  type Invoice,
  type InvoicePayload,
  type WorkOption,
  type InvoiceFilterOptions,
  type InvoiceTotals,
  type RevenueInvoiceFilters,
} from "../../faturas/faturasApi";
import {
  ColumnFilterAmount,
  ColumnFilterDateRange,
  ColumnFilterEmpty,
  ColumnFilterSelect,
  ColumnFilterText,
  SortHeader,
  SrOnly,
  toAmount,
  toLocalDayIso,
  totalsLabel,
} from "../../faturas/ColumnFilters";
import { formatArrival, rowAmounts, sliceForWork } from "../../faturas/invoiceDisplay";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import {
  SelectionTotalsRow,
  useInvoiceSelection,
  type SelectedInvoiceLine,
} from "../../faturas/InvoiceSelection";

// Faturas de RECEITA: faturas emitidas pela AJA2 (o emitente somos nós), captadas por
// importação/upload e classificadas automaticamente (emitente = NIF da AJA2 → receita). Espelho de
// "Faturas Despesas", do lado da receita. A gestão de recebimentos/aging fica em Contas a Receber.

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Com vírgula decimal, os pontos são separadores de milhares ("1.234,56"); sem vírgula, um
  // ponto continua a valer como decimal ("1.5") — remover pontos às cegas transformava-o em 15.
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Sessão expirada. Autentique-se novamente.";
    if (error.status === 403) return "Sem permissões para ver as faturas de receita.";
    if (error.message) {
      try {
        const parsed = JSON.parse(error.message);
        if (parsed && typeof parsed === "object") {
          return parsed.detail || parsed.title || error.message;
        }
      } catch {
        // não é JSON — usa o texto tal como veio.
      }
      return error.message;
    }
  }
  return "Não foi possível carregar as faturas de receita.";
};

type RevenueEditForm = {
  companyId: string;
  workId: string;
  client: string;
  clientTaxNumber: string;
  documentNumber: string;
  documentDate: string;
  description: string;
  baseAmount: string;
  vatAmount: string;
  totalAmount: string;
  paidAmount: string;
};

export function FaturasReceitasView() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Depois do primeiro load a tabela fica sempre montada (o loader passa a esmaecimento) — os
  // filtros vivem no thead, e desmontá-la a cada reload matava o foco/popovers a meio da escrita.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  // Filtros de coluna (estilo Excel). Vazio = coluna sem filtro. Todos vão à query — um filtro
  // só do lado do cliente dessincronizava o total, que é calculado no servidor.
  const [workFilter, setWorkFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  // Estado de Pagamento (a mesma tabela das despesas); "" = todos, "sem-estado" = sem estado.
  const [statusFilter, setStatusFilter] = useState("");
  // Filtro "Fora do Resumo": liga-se para listar só as linhas excluídas do Resumo do ano.
  const [onlyExcludedFromSummary, setOnlyExcludedFromSummary] = useState(false);
  const [statuses, setStatuses] = useState<ApprovalStatusOption[]>([]);

  // Edição de receitas — todos os campos editáveis, como nas despesas. O payload preenche-se
  // sempre a partir da fatura atual: o UpdateInvoice sobrescreve tudo o que receber, e um campo
  // omitido à toa apagava o valor existente.
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editForm, setEditForm] = useState<RevenueEditForm | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [savingSummaryExclusion, setSavingSummaryExclusion] = useState(false);
  const [isTogglingSelectionExclusion, setIsTogglingSelectionExclusion] = useState(false);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [viewerDocument, setViewerDocument] = useState<ViewerDocument | null>(null);
  const [works, setWorks] = useState<WorkOption[]>([]);
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [createdFromFilter, setCreatedFromFilter] = useState("");
  const [createdToFilter, setCreatedToFilter] = useState("");
  // Como nas despesas: por omissão a data do documento; "entrada" para quem procura o que acabou
  // de chegar. Clicar na coluna já activa inverte o sentido.
  const [sort, setSort] = useState<"documento" | "entrada">("documento");
  const [sortAscending, setSortAscending] = useState(false);

  const toggleSort = (coluna: "documento" | "entrada") => {
    if (coluna === sort) {
      setSortAscending((anterior) => !anterior);
      return;
    }
    setSort(coluna);
    setSortAscending(false);
  };
  const [documentNumberFilter, setDocumentNumberFilter] = useState("");
  const [netFilter, setNetFilter] = useState("");
  const [grossFilter, setGrossFilter] = useState("");
  const [documentsFilter, setDocumentsFilter] = useState("");

  // Opções dos dropdowns: só o que existe mesmo nas faturas de receita.
  const [companies, setCompanies] = useState<Company[]>([]);
  // "" = todas as empresas (vista combinada).
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [filterOptions, setFilterOptions] = useState<InvoiceFilterOptions>({
    entities: [],
    subTypes: [],
    works: [],
  });

  // Totais do conjunto filtrado inteiro, vindos do servidor. Antes somava-se `invoices` no
  // cliente, o que só contava a página carregada e ficava a menos acima de 200 faturas.
  const [totals, setTotals] = useState<InvoiceTotals>({
    baseTotal: 0,
    vatTotal: 0,
    grandTotal: 0,
    count: 0,
  });

  // Os filtros ativos, construídos num único sítio — a listagem e a exportação partilham este
  // objeto para o ficheiro exportado não poder divergir da grelha.
  const activeFilters = useMemo<RevenueInvoiceFilters>(
    () => ({
      companyId: companyFilter ? Number(companyFilter) : null,
      search: search || undefined,
      workId: workFilter ? Number(workFilter) : null,
      entity: clientFilter || null,
      approvalStatusCode: statusFilter || null,
      from: dateFromFilter || null,
      to: dateToFilter || null,
      createdFrom: toLocalDayIso(createdFromFilter),
      createdTo: toLocalDayIso(createdToFilter),
      sort,
      sortAscending,
      documentNumber: documentNumberFilter || null,
      net: toAmount(netFilter),
      gross: toAmount(grossFilter),
      documents: documentsFilter || null,
      excludedFromSummary: onlyExcludedFromSummary ? true : null,
    }),
    [
      companyFilter,
      search,
      workFilter,
      clientFilter,
      statusFilter,
      dateFromFilter,
      dateToFilter,
      createdFromFilter,
      createdToFilter,
      sort,
      sortAscending,
      documentNumberFilter,
      netFilter,
      grossFilter,
      documentsFilter,
      onlyExcludedFromSummary,
    ],
  );

  // Nº de sequência dos loads: com vários inputs debounced, dois pedidos podem sobrepor-se e o
  // mais antigo chegar por último — sem a guarda, repintava grelha e rodapé com filtros já velhos.
  const loadSequenceRef = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++loadSequenceRef.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getRevenueInvoices({ ...activeFilters, pageSize: 200 });
      if (loadSequenceRef.current !== sequence) return;
      setInvoices(result.items);
      setTotals(result.totals);
    } catch (error) {
      if (loadSequenceRef.current !== sequence) return;
      setInvoices([]);
      setTotals({ baseTotal: 0, vatTotal: 0, grandTotal: 0, count: 0 });
      setLoadError(parseErrorMessage(error));
    } finally {
      if (loadSequenceRef.current === sequence) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [activeFilters]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const handleStatusChange = async (invoice: Invoice, statusCode: string) => {
    try {
      await setInvoiceApprovalStatus(invoice.id, statusCode);
      await load();
    } catch (error) {
      setLoadError(parseErrorMessage(error));
    }
  };

  // Todas as obras (não só as com receitas): editar é poder apontar para uma obra ainda sem faturas.
  useEffect(() => {
    let active = true;
    getWorks()
      .then((all) => {
        if (active) setWorks(all);
      })
      .catch(() => {
        // Sem obras o seletor fica só com "Sem obra" — a edição continua utilizável.
      });
    return () => {
      active = false;
    };
  }, []);

  const editWorkOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Sem obra" },
      ...works.map((work) => ({
        value: String(work.id),
        label: work.code ? `${work.code} — ${work.name}` : work.name,
      })),
    ],
    [works],
  );

  const openEdit = (invoice: Invoice) => {
    setSaveNotice(null);
    setEditingInvoice(invoice);
    setEditForm({
      companyId: invoice.companyId != null ? String(invoice.companyId) : "",
      workId: invoice.workId != null ? String(invoice.workId) : "",
      client: invoice.matchedSupplierName ?? invoice.supplierName ?? "",
      clientTaxNumber: invoice.supplierTaxNumber ?? "",
      documentNumber: invoice.documentNumber ?? "",
      documentDate: invoice.documentDate ? invoice.documentDate.slice(0, 10) : "",
      description: invoice.description ?? "",
      baseAmount: invoice.baseAmount != null ? String(invoice.baseAmount) : "",
      vatAmount: invoice.vatAmount != null ? String(invoice.vatAmount) : "",
      totalAmount: invoice.totalAmount != null ? String(invoice.totalAmount) : "",
      paidAmount: invoice.paidAmount != null ? String(invoice.paidAmount) : "",
    });
    setEditError(null);
  };

  const closeEdit = () => {
    setEditingInvoice(null);
    setEditForm(null);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editingInvoice || !editForm || isSavingEdit) return;
    if (!editForm.client.trim()) {
      setEditError("Indique o cliente.");
      return;
    }
    const baseAmount = parseNumber(editForm.baseAmount);
    const totalAmount = parseNumber(editForm.totalAmount);
    if (baseAmount === null || totalAmount === null) {
      setEditError("Preencha o valor s/IVA e o valor c/IVA.");
      return;
    }
    // Mudar o nome à mão desliga o cliente casado — o texto novo é que manda; sem mudança, o
    // casamento mantém-se e o backend continua a mostrar o nome da ficha.
    // Comparação normalizada: re-escrever o mesmo nome com outra capitalização ou espaços não é
    // "mudar o cliente" — só uma alteração real ao nome desliga a ficha casada.
    const normalizeName = (value: string) =>
      value.trim().replace(/\s+/g, " ").toLowerCase();
    const originalClient =
      editingInvoice.matchedSupplierName ?? editingInvoice.supplierName ?? "";
    const matchedSupplierId =
      normalizeName(editForm.client) === normalizeName(originalClient)
        ? (editingInvoice.matchedSupplierId ?? null)
        : null;
    setIsSavingEdit(true);
    setEditError(null);
    try {
      const payload: InvoicePayload = {
        companyId: editForm.companyId ? Number(editForm.companyId) : null,
        workId: editForm.workId ? Number(editForm.workId) : null,
        expenseCategoryId: editingInvoice.expenseCategoryId ?? null,
        subType: null,
        supplierName: matchedSupplierId ? null : editForm.client.trim() || null,
        supplierTaxNumber: matchedSupplierId
          ? null
          : editForm.clientTaxNumber.trim() || null,
        matchedSupplierId,
        documentNumber: editForm.documentNumber.trim() || null,
        documentDate: editForm.documentDate
          ? new Date(editForm.documentDate).toISOString()
          : null,
        description: editForm.description.trim() || null,
        baseAmount,
        vatAmount: parseNumber(editForm.vatAmount),
        totalAmount,
        paidAmount: parseNumber(editForm.paidAmount),
        currencyCode: editingInvoice.currencyCode ?? null,
        // allocations omitido = as repartições existentes ficam como estão
      };
      const saved = await updateInvoice(editingInvoice.id, payload);
      closeEdit();
      setSaveNotice("Fatura de receita guardada.");
      // A folha segue a fatura: gravar noutra empresa com o seletor filtrado fazia-a desaparecer.
      // Mudar o filtro já dispara o reload (debounce) com o valor NOVO — chamar load() aqui
      // usava o closure com o filtro antigo e refazia a busca errada.
      if (
        companyFilter &&
        saved.companyId != null &&
        String(saved.companyId) !== companyFilter
      ) {
        setCompanyFilter(String(saved.companyId));
      } else {
        await load();
      }
    } catch (error) {
      setEditError(parseErrorMessage(error));
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Opções dos dropdowns de coluna. Carregadas uma vez — são o universo do lado da receita e não
  // variam com os filtros aplicados.
  useEffect(() => {
    let active = true;
    getCompanies()
      .then((comps) => {
        if (active) setCompanies(comps);
      })
      .catch(() => {
        // Sem empresas o seletor não aparece — a listagem continua utilizável.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    // A mesma tabela de Estados de Pagamento das despesas (Definições → Estados de Pagamento).
    getApprovalStatuses()
      .then((sts) => {
        if (active) setStatuses(sts);
      })
      .catch(() => {
        // Sem a lista o dropdown mostra só o estado atual de cada linha — a listagem continua utilizável.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getInvoiceFilterOptions(true)
      .then((options) => {
        if (active) setFilterOptions(options);
      })
      .catch(() => {
        // Sem opções os dropdowns ficam só com "Todos" — a listagem continua utilizável.
      });
    return () => {
      active = false;
    };
  }, []);

  // Filtros de coluna Cliente/Obra: cardinalidade alta → seletor com pesquisa. A fonte mantém-se
  // o `filterOptions` (só o que tem faturas de receita); a opção vazia é o "sem filtro".
  const clientFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todos" },
      ...filterOptions.entities.map((name) => ({ value: name, label: name })),
    ],
    [filterOptions.entities],
  );

  const workFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todas" },
      ...filterOptions.works.map((work) => ({
        value: String(work.id),
        label: work.code ? `${work.code} — ${work.name}` : work.name,
      })),
    ],
    [filterOptions.works],
  );

  const hasColumnFilters = Boolean(
    workFilter ||
      clientFilter ||
      statusFilter ||
      dateFromFilter ||
      dateToFilter ||
      createdFromFilter ||
      createdToFilter ||
      documentNumberFilter ||
      netFilter ||
      grossFilter ||
      documentsFilter ||
      onlyExcludedFromSummary,
  );

  const clearColumnFilters = useCallback(() => {
    setWorkFilter("");
    setClientFilter("");
    setStatusFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setCreatedFromFilter("");
    setCreatedToFilter("");
    setDocumentNumberFilter("");
    setNetFilter("");
    setGrossFilter("");
    setDocumentsFilter("");
    setOnlyExcludedFromSummary(false);
  }, []);

  // Obra do filtro de coluna, como número — as linhas repartidas mostram a fatia desta obra.
  const filteredWorkId = workFilter ? Number(workFilter) : null;

  // Linha da seleção: os valores mostrados (fatia, se há filtro de obra).
  const lineFor = (invoice: Invoice): SelectedInvoiceLine => {
    const amounts = rowAmounts(invoice, filteredWorkId);
    return {
      id: invoice.id,
      base: amounts.base ?? 0,
      total: amounts.total ?? 0,
    };
  };

  // Seleção manual de linhas com total próprio (à Excel), sobre a listagem atual — mudar os
  // filtros retira as linhas que saíram do resultado (ver InvoiceSelection.tsx).
  const selection = useInvoiceSelection(invoices.map(lineFor));

  // Só quando a seleção inteira já está fora do Resumo é que o gesto de grupo se inverte.
  const selectionAllExcluded =
    selection.count > 0 &&
    invoices
      .filter((invoice) => selection.ids.includes(invoice.id))
      .every((invoice) => invoice.excludeFromSummary);

  // Excluir do Resumo é uma decisão leve e reversível, gravada na hora (não espera pelo
  // Guardar): a linha continua viva em todo o lado, só deixa de somar no Resumo do ano.
  const handleSetSummaryExclusion = async (excluded: boolean) => {
    if (!editingInvoice || savingSummaryExclusion) return;
    setSavingSummaryExclusion(true);
    setEditError(null);
    try {
      const updated = await setInvoiceSummaryExclusion(editingInvoice.id, excluded);
      setEditingInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setEditError(parseErrorMessage(error));
    } finally {
      setSavingSummaryExclusion(false);
    }
  };

  const handleSetSelectedSummaryExclusion = async (excluded: boolean) => {
    const ids = selection.ids;
    if (ids.length === 0 || isTogglingSelectionExclusion) return;
    setIsTogglingSelectionExclusion(true);
    setActionError(null);
    try {
      await setInvoicesSummaryExclusion(ids, excluded);
      setInvoices((prev) =>
        prev.map((item) =>
          ids.includes(item.id) ? { ...item, excludeFromSummary: excluded } : item,
        ),
      );
      setEditingInvoice((current) =>
        current && ids.includes(current.id)
          ? { ...current, excludeFromSummary: excluded }
          : current,
      );
      setActionNotice(
        excluded
          ? `${ids.length} linha(s) fora do Resumo do ano`
          : `${ids.length} linha(s) voltam a contar no Resumo do ano`,
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsTogglingSelectionExclusion(false);
    }
  };

  const [isExporting, setIsExporting] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Fecha o menu de exportação ao clicar fora dele (mesmo padrão das Faturas Despesas).
  useEffect(() => {
    if (!isExportMenuOpen) return;
    const handler = (event: MouseEvent) => {
      if (!exportMenuRef.current?.contains(event.target as Node)) {
        setIsExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isExportMenuOpen]);

  const handleExport = async (format: "excel" | "csv") => {
    setIsExportMenuOpen(false);
    setIsExporting(true);
    try {
      // Os mesmos filtros da listagem; com linhas marcadas, exporta só essas.
      await exportRevenueInvoices(
        activeFilters,
        format,
        selection.count > 0 ? selection.ids : undefined,
      );
    } catch (error) {
      setExportError(parseErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  };

  const actions = (
    <button
      className="px-4 py-2 border border-border rounded transition-colors flex items-center gap-2 hover:bg-accent/30 disabled:opacity-50"
      style={{ borderRadius: "var(--radius-button)", height: "var(--button-height)" }}
      onClick={load}
      disabled={isLoading}
    >
      <ArrowSync20Regular className="w-5 h-5" />
      Atualizar
    </button>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Faturas Receitas"
        subtitle="Faturas emitidas pela AJA2 a clientes (receita), captadas dos documentos e classificadas automaticamente."
        actions={actions}
      />

      <div className="p-4 border-b border-border bg-card flex flex-wrap gap-3 items-center">
        {companies.length > 1 && (
          <select
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
            title="Empresa do grupo que emitiu a fatura."
            className="px-3 py-2 border border-border rounded bg-background text-foreground font-medium"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <option value="">Todas as empresas</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        )}

        <div className="relative flex-1 min-w-[220px]">
          <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Procurar por cliente ou valor..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </div>

        {/* Lista só as linhas marcadas "fora do Resumo" — sem isto só se encontravam uma a
            uma, à procura do badge no meio da lista. */}
        <button
          type="button"
          aria-pressed={onlyExcludedFromSummary}
          onClick={() => setOnlyExcludedFromSummary((prev) => !prev)}
          className={`px-3 py-2 text-sm border cursor-pointer ${
            onlyExcludedFromSummary
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-foreground hover:bg-accent/30"
          }`}
          style={{ borderRadius: "var(--radius-button)" }}
        >
          Fora do Resumo
        </button>

        {/* Obra, cliente e estado filtram-se na respetiva coluna da tabela (estilo Excel). O botão
            aparece também quando há texto na barra de pesquisa e, ao limpar, apaga-a com o resto. */}
        {(hasColumnFilters || search) && (
          <button
            type="button"
            onClick={() => {
              clearColumnFilters();
              setSearch("");
            }}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Limpar filtros
          </button>
        )}

        {/* Fora do Resumo em grupo: quando a seleção inteira já está fora, o gesto inverte-se. */}
        {selection.count > 0 && (
          <button
            type="button"
            onClick={() => void handleSetSelectedSummaryExclusion(!selectionAllExcluded)}
            disabled={isTogglingSelectionExclusion || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isTogglingSelectionExclusion
              ? "A marcar…"
              : selectionAllExcluded
                ? `Contar no Resumo (${selection.count})`
                : `Fora do Resumo (${selection.count})`}
          </button>
        )}

        {/* Um só botão de exportação; o formato escolhe-se no menu. Sem seleção exporta o conjunto
            filtrado inteiro; com linhas marcadas, só essas. Espelho das Faturas Despesas. */}
        <div className="relative" ref={exportMenuRef}>
          <button
            type="button"
            onClick={() => setIsExportMenuOpen((prev) => !prev)}
            disabled={isExporting || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50 flex items-center gap-1"
            style={{ borderRadius: "var(--radius-button)" }}
            aria-haspopup="menu"
            aria-expanded={isExportMenuOpen}
          >
            <ArrowDownload20Regular className="w-4 h-4" />
            {isExporting
              ? "A exportar…"
              : selection.count > 0
                ? `Exportar seleção (${selection.count})`
                : "Exportar"}
            <ChevronDown20Regular className="w-4 h-4 text-secondary-text" />
          </button>
          {isExportMenuOpen && (
            <div
              className="absolute right-0 mt-1 bg-card border border-border shadow-lg z-50"
              style={{ borderRadius: "var(--radius-card)", minWidth: "10rem" }}
            >
              <button
                type="button"
                onClick={() => handleExport("excel")}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent text-foreground"
              >
                Excel (.xlsx)
              </button>
              <button
                type="button"
                onClick={() => handleExport("csv")}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent text-foreground"
              >
                CSV (.csv)
              </button>
            </div>
          )}
        </div>
      </div>

      {exportError && (
        <div
          className="px-4 py-2 border-b border-border flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <span className="break-all">{exportError}</span>
          <Tooltip text="Dispensar">
            <button
              className="ml-auto p-1 rounded hover:bg-accent/30"
              onClick={() => setExportError(null)}
            >
              <Dismiss20Regular className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      )}

      {actionNotice && (
        <div
          className="px-4 py-2 border-b border-border flex items-center gap-2 text-sm"
          style={{ color: "var(--primary)" }}
        >
          <CheckmarkCircle20Regular className="w-5 h-5 flex-shrink-0" />
          <span className="break-all">{actionNotice}</span>
          <Tooltip text="Dispensar">
            <button
              className="ml-auto p-1 rounded hover:bg-accent/30"
              onClick={() => setActionNotice(null)}
            >
              <Dismiss20Regular className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      )}

      {actionError && (
        <div
          className="px-4 py-2 border-b border-border flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <span className="break-all">{actionError}</span>
          <Tooltip text="Dispensar">
            <button
              className="ml-auto p-1 rounded hover:bg-accent/30"
              onClick={() => setActionError(null)}
            >
              <Dismiss20Regular className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading && !hasLoadedOnce && (
          <div className="p-8 text-center text-secondary-text">A carregar…</div>
        )}

        {!isLoading && loadError && (
          <div className="p-8 text-center" style={{ color: "var(--destructive)" }}>
            {loadError}
          </div>
        )}

        {/* A tabela desenha-se mesmo sem linhas: é no cabeçalho que vivem os filtros, e escondê-la
            ao filtrar para zero resultados deixava o utilizador sem forma de os limpar. Nos
            reloads seguintes fica montada (só esmaece) — desmontá-la matava o foco dos inputs de
            filtro e fechava os popovers a cada tecla, com o debounce a disparar a meio da escrita. */}
        {(hasLoadedOnce || !isLoading) && !loadError && (
          <table
            className="w-full text-sm"
            style={{ minWidth: 980, opacity: isLoading ? 0.6 : 1 }}
            aria-busy={isLoading}
          >
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-secondary-text">
                <th className="px-3 py-2 font-medium" style={{ width: 36 }}>
                  <Tooltip text="Selecionar as linhas visíveis">
                    <input
                      type="checkbox"
                      checked={selection.allSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            !selection.allSelected && selection.anySelected;
                        }
                      }}
                      onChange={selection.toggleAll}
                    />
                  </Tooltip>
                </th>
                <th className="px-3 py-2 font-medium" style={{ width: 150 }}>Nº</th>
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Obra</th>
                <SortHeader
                  label="Data"
                  active={sort === "documento"}
                  ascending={sortAscending}
                  title="Ordenar pela data impressa no documento. Clicar outra vez inverte."
                  onClick={() => toggleSort("documento")}
                />
                <SortHeader
                  label="Entrada"
                  active={sort === "entrada"}
                  ascending={sortAscending}
                  title="Quando a fatura entrou no sistema. Põe em cima o que acabou de chegar, mesmo que o documento tenha data antiga. Clicar outra vez inverte."
                  onClick={() => toggleSort("entrada")}
                />
                <th className="px-3 py-2 font-medium text-right" style={{ width: 130 }}>s/IVA</th>
                <th className="px-3 py-2 font-medium text-right" style={{ width: 130 }}>c/IVA</th>
                <th className="px-3 py-2 font-medium" style={{ width: 150 }}>Estado</th>
                <th className="px-3 py-2 font-medium text-center" style={{ width: 90 }}>Doc.</th>
                <th className="px-3 py-2 font-medium" style={{ width: 44 }}>
                  <SrOnly>Ações</SrOnly>
                </th>
              </tr>
              {/* Filtros por coluna (Excel): cada um vai à query, para o total do rodapé
                  acompanhar sempre o que está filtrado. */}
              <tr className="text-left">
                <ColumnFilterEmpty />
                <ColumnFilterText
                  value={documentNumberFilter}
                  onChange={setDocumentNumberFilter}
                  placeholder="Contém…"
                />
                {/* Cliente e Obra são as colunas de cardinalidade alta — seletor com pesquisa
                    em vez do <select> nativo. Compacto via tokens (o CSS é pré-compilado, classes
                    novas não existem): o tamanho de letra desce para o dos restantes filtros e,
                    com filtro ativo, a moldura fica primária (o input herda o token --border). */}
                <th className="px-3 pb-2 font-normal align-top">
                  <SearchableSelect<string>
                    ariaLabel="Cliente"
                    value={clientFilter}
                    onChange={(next) => setClientFilter(next ?? "")}
                    options={clientFilterSelectOptions}
                    allowClear={false}
                    searchPlaceholder="Procurar…"
                    panelPosition="fixed"
                    className={clientFilter ? "font-medium" : undefined}
                    style={
                      {
                        minWidth: "6.5rem",
                        "--text-base": "var(--text-caption)",
                        ...(clientFilter
                          ? { "--border": "var(--primary)" }
                          : null),
                      } as CSSProperties
                    }
                  />
                </th>
                <th className="px-3 pb-2 font-normal align-top">
                  <Tooltip text="Filtrar por obra. Nas faturas repartidas o total conta só a fatia desta obra.">
                    <div>
                      <SearchableSelect<string>
                        ariaLabel="Obra"
                        value={workFilter}
                        onChange={(next) => setWorkFilter(next ?? "")}
                        options={workFilterSelectOptions}
                        allowClear={false}
                        searchPlaceholder="Procurar…"
                        panelPosition="fixed"
                        className={workFilter ? "font-medium" : undefined}
                        style={
                          {
                            minWidth: "6.5rem",
                            "--text-base": "var(--text-caption)",
                            ...(workFilter
                              ? { "--border": "var(--primary)" }
                              : null),
                          } as CSSProperties
                        }
                      />
                    </div>
                  </Tooltip>
                </th>
                <ColumnFilterDateRange
                  from={dateFromFilter}
                  to={dateToFilter}
                  onChange={(from, to) => {
                    setDateFromFilter(from);
                    setDateToFilter(to);
                  }}
                  title="Filtrar por data do documento (De/Até, inclusivo)."
                />
                <ColumnFilterDateRange
                  from={createdFromFilter}
                  to={createdToFilter}
                  onChange={(from, to) => {
                    setCreatedFromFilter(from);
                    setCreatedToFilter(to);
                  }}
                  title="Filtrar por data de entrada no sistema (De/Até, inclusivo) — quando a fatura cá chegou, não a data que traz impressa."
                />
                <ColumnFilterAmount
                  value={netFilter}
                  onChange={setNetFilter}
                  title="Filtrar pelo valor s/IVA. Com filtro de obra compara o documento completo, não a fatia da obra."
                />
                <ColumnFilterAmount
                  value={grossFilter}
                  onChange={setGrossFilter}
                  title="Filtrar pelo valor c/IVA. Com filtro de obra compara o documento completo, não a fatia da obra."
                />
                <ColumnFilterSelect
                  value={statusFilter}
                  label="Estado"
                  onChange={setStatusFilter}
                  allLabel="Todos"
                  title="Filtrar pelo Estado de Pagamento (a mesma lista das despesas, em Definições → Estados de Pagamento)."
                  options={[
                    { value: "sem-estado", label: "Sem estado" },
                    ...statuses.map((status) => ({
                      value: status.code,
                      label: status.name,
                    })),
                  ]}
                />
                <ColumnFilterSelect
                  value={documentsFilter}
                  label="Documentos"
                  onChange={setDocumentsFilter}
                  allLabel="Todos"
                  title="Filtrar por faturas com ou sem documento (PDF) anexo."
                  options={[
                    { value: "with-document", label: "Com documento" },
                    { value: "without-document", label: "Sem documento" },
                  ]}
                />
                <ColumnFilterEmpty />
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-secondary-text">
                    {hasColumnFilters || search
                      ? "Sem faturas de receita para os filtros aplicados."
                      : "Sem faturas de receita. Faturas emitidas pela AJA2 (em que o emitente é a AJA2) aparecem aqui automaticamente quando são importadas ou submetidas."}
                  </td>
                </tr>
              )}
              {invoices.map((invoice, index) => {
                // Com filtro de obra, uma fatura repartida mostra a fatia dessa obra — as linhas
                // visíveis passam a somar o total do rodapé.
                const slice = sliceForWork(invoice, filteredWorkId);
                const amounts = rowAmounts(invoice, filteredWorkId);
                return (
                <tr
                  key={invoice.id}
                  className={`cursor-pointer border-b border-border hover:bg-accent/30 ${
                    index % 2 === 1 ? "bg-muted" : ""
                  }`}
                  onClick={() => openEdit(invoice)}
                >
                  <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.isSelected(invoice.id)}
                      onChange={() => selection.toggle(invoice.id)}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {invoice.documentNumber || "—"}
                      {invoice.sourceChannel === "email" && (
                        <Tooltip
                          text={`Recebido por email${invoice.sourceSender ? ` de ${invoice.sourceSender}` : ""}`}
                        >
                          <span className="text-secondary-text flex-shrink-0">
                            ✉
                          </span>
                        </Tooltip>
                      )}
                      {invoice.excludeFromSummary && (
                        <>
                          <Tooltip text="Não conta no Resumo do ano, por decisão manual. Muda-se ao abrir a linha.">
                            <span
                              className="inline-flex items-center px-2 py-0.5 text-xs rounded flex-shrink-0"
                              style={{ color: "var(--secondary-text)", backgroundColor: "rgba(100,116,139,0.12)" }}
                            >
                              Fora do Resumo
                            </span>
                          </Tooltip>
                          <SrOnly>{" Não conta no Resumo do ano, por decisão manual. "}</SrOnly>
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {invoice.matchedSupplierName || invoice.supplierName || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {slice ? (slice.workName ?? "—") : invoice.workName || "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateShort(invoice.documentDate)}</td>
                  <Tooltip
                    text={`Entrou no sistema a ${new Date(invoice.created).toLocaleString(APP_LOCALE)}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatArrival(invoice.created)}
                      {/* "Hoje" e "Ontem" poupam a leitura a quem vê a tabela, mas escondiam a data
                          exacta de quem usa leitor de ecrã: ela só existia na dica do rato, que um
                          <td> não alcança pelo teclado. Aqui fica sempre, sem tomar espaço nem
                          acrescentar uma paragem de tabulação por linha. */}
                      <SrOnly>
                        {` (entrou a ${new Date(invoice.created).toLocaleString(APP_LOCALE)})`}
                      </SrOnly>
                    </td>
                  </Tooltip>
                  <Tooltip
                    text={
                      amounts.isSlice
                        ? `Fatia desta obra · documento completo: ${formatCurrency(invoice.baseAmount)}`
                        : null
                    }
                  >
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatCurrency(amounts.base)}
                    </td>
                  </Tooltip>
                  <Tooltip
                    text={
                      amounts.isSlice
                        ? `Fatia desta obra · documento completo: ${formatCurrency(invoice.totalAmount)}`
                        : null
                    }
                  >
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {formatCurrency(amounts.total)}
                    </td>
                  </Tooltip>
                  <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                    <select
                      value={invoice.approvalStatusCode}
                      onChange={(event) =>
                        handleStatusChange(invoice, event.target.value)
                      }
                      className="px-2 py-1 border border-border rounded bg-background text-foreground text-xs"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      {/* "sem-estado" é o sentinela do backend para NULL — escolher limpa o estado. */}
                      <option value="sem-estado">Sem estado</option>
                      {statuses.length === 0 &&
                        invoice.approvalStatusCode !== "sem-estado" && (
                          <option value={invoice.approvalStatusCode}>
                            {invoice.approvalStatusName}
                          </option>
                        )}
                      {statuses.map((status) => (
                        <option key={status.id} value={status.code}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center" onClick={(event) => event.stopPropagation()}>
                    {invoice.hasDocument ? (
                      <Tooltip text="Ver o documento">
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent/30"
                          style={{ borderRadius: "var(--radius-button)" }}
                          onClick={() =>
                            setViewerDocument({
                              name: invoice.documentFileName ?? "Fatura",
                              load: () => fetchInvoiceDocument(invoice),
                            })
                          }
                        >
                          <DocumentPdf20Regular className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center" onClick={(event) => event.stopPropagation()}>
                    <Tooltip text="Editar a fatura">
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent/30"
                        style={{ borderRadius: "var(--radius-button)" }}
                        aria-label={`Editar a fatura ${invoice.documentNumber ?? invoice.matchedSupplierName ?? invoice.supplierName ?? ""}`}
                        onClick={() => openEdit(invoice)}
                      >
                        <Edit20Regular className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </td>
                </tr>
                );
              })}
            </tbody>
            {/* Totais do conjunto filtrado inteiro (vêm somados do servidor), não apenas das
                linhas desenhadas — a grelha pára nas 200 e a soma das visíveis enganaria. */}
            <tfoot className="sticky bottom-0 bg-card border-t-2 border-border font-medium">
              <SelectionTotalsRow
                selection={selection}
                labelColSpan={6}
                trailingColSpan={3}
              />
              <tr>
                <td className="px-3 py-2 text-secondary-text" colSpan={6}>
                  {totalsLabel(totals)}
                  {workFilter && " · só a fatia desta obra nas repartidas"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatCurrency(totals.baseTotal)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatCurrency(totals.grandTotal)}
                </td>
                <td className="px-3 py-2" colSpan={3} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Sempre montada: uma live region só anuncia se já existir quando o texto muda. O aviso
          é só para leitores de ecrã — visualmente o fecho da modal e a linha atualizada chegam. */}
      <div role="status">
        <SrOnly>{saveNotice ?? ""}</SrOnly>
      </div>

      {editingInvoice && editForm && (
        <Modal
          title="Editar fatura de receita"
          onClose={closeEdit}
          footer={
            <>
              <button
                className="px-4 py-2 border border-border rounded text-foreground hover:bg-accent/30"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={closeEdit}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="edit-receita-form"
                className="px-4 py-2 rounded text-primary-foreground disabled:opacity-50"
                style={{
                  borderRadius: "var(--radius-button)",
                  backgroundColor: "var(--primary)",
                }}
                disabled={isSavingEdit}
                aria-busy={isSavingEdit}
              >
                {isSavingEdit ? "A guardar…" : "Guardar"}
              </button>
            </>
          }
        >
          <p className="text-sm text-secondary-text">
            {editingInvoice.documentNumber ?? "Sem nº de documento"}
            {" · * campo obrigatório"}
          </p>
          <form
            id="edit-receita-form"
            onSubmit={(event) => {
              event.preventDefault();
              saveEdit();
            }}
          >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Empresa</span>
                <select
                  value={editForm.companyId}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, companyId: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  <option value="">— Sem empresa —</option>
                  {companies.map((company) => (
                    <option key={company.id} value={String(company.id)}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-1">
                <SearchableSelect<string>
                  label="Obra"
                  labelClassName="text-sm text-secondary-text"
                  value={editForm.workId}
                  onChange={(next) =>
                    setEditForm((prev) => prev && { ...prev, workId: next ?? "" })
                  }
                  options={editWorkOptions}
                  allowClear={false}
                  searchPlaceholder="Procurar…"
                  panelPosition="fixed"
                />
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Cliente *</span>
                <input
                  type="text"
                  aria-required="true"
                  aria-invalid={editError != null && !editForm.client.trim()}
                  value={editForm.client}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, client: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
                {editingInvoice.matchedSupplierId != null && (
                  <span className="text-secondary-text" style={{ fontSize: "var(--text-xs)" }}>
                    Ligado à ficha do cliente — mudar o nome desliga a ligação e passa a valer o
                    texto escrito.
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">NIF do cliente</span>
                <input
                  type="text"
                  value={editForm.clientTaxNumber}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, clientTaxNumber: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Nº documento</span>
                <input
                  type="text"
                  value={editForm.documentNumber}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, documentNumber: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Data do documento</span>
                <input
                  type="date"
                  value={editForm.documentDate}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, documentDate: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-sm text-secondary-text">Descritivo</span>
                <textarea
                  value={editForm.description}
                  rows={3}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, description: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Valor s/IVA *</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-required="true"
                  aria-invalid={editError != null && parseNumber(editForm.baseAmount) === null}
                  value={editForm.baseAmount}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, baseAmount: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">IVA</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={editForm.vatAmount}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, vatAmount: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Valor c/IVA *</span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-required="true"
                  aria-invalid={editError != null && parseNumber(editForm.totalAmount) === null}
                  value={editForm.totalAmount}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, totalAmount: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-secondary-text">Valor recebido</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={editForm.paidAmount}
                  onChange={(event) =>
                    setEditForm((prev) => prev && { ...prev, paidAmount: event.target.value })
                  }
                  className="px-3 py-2 border border-border rounded bg-background text-foreground"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
              </label>
          </div>
          {/* Fora do Resumo grava na hora (não espera pelo Guardar) — a mesma caixa da janela de
              Documentos das despesas, do lado das receitas. */}
          <div aria-busy={savingSummaryExclusion} className="text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={editingInvoice.excludeFromSummary ?? false}
                disabled={savingSummaryExclusion}
                onChange={(event) => void handleSetSummaryExclusion(event.target.checked)}
              />
              Não contar esta linha no Resumo do ano
            </label>
          </div>
          {/* Sempre montado: uma live region só anuncia se já existir quando o texto muda. */}
          <div role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {editError}
          </div>
          </form>
        </Modal>
      )}

      {viewerDocument && (
        <DocumentViewer
          document={viewerDocument}
          onClose={() => setViewerDocument(null)}
        />
      )}
    </div>
  );
}
